import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  EmptyState,
  Skeleton,
  Tag
} from '../primitives';
import { supabase } from '../../lib/supabase';
import {
  AttachWorkItemCover,
  type AttachWorkItemRow,
  type AttachWorkItemType
} from '../AttachWorkItemCover';
import type { Database } from '../../types/database';

type WorkItemRow = Pick<
  Database['public']['Tables']['work_items']['Row'],
  'id' | 'name' | 'type' | 'status' | 'updated_at' | 'org_id'
>;

interface LinkedWorkItem {
  id: string;
  name: string;
  type: AttachWorkItemType;
  status: string;
  updatedAt: string;
}

export interface AttachedWorkItemsPanelProps {
  documentId: string;
  orgId: string;
  onError: (message: string) => void;
  /**
   * When true the panel renders only its inner list — the parent supplies the
   * chrome (border, padding, title). The "+ Attach to Work Item" action moves
   * inline above the list because there's no Card head to host it.
   */
  naked?: boolean;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; rows: LinkedWorkItem[] };

/**
 * Attached-to-Work-Items panel for `/document-editor/:id`. Reads
 * `work_item_document_links` joined with `work_items` and lets the user attach
 * additional work items via the AttachWorkItemCover primitive.
 */
export function AttachedWorkItemsPanel({
  documentId,
  orgId,
  onError,
  naked = false
}: AttachedWorkItemsPanelProps) {
  const navigate = useNavigate();
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [coverOpen, setCoverOpen] = useState(false);
  const [candidates, setCandidates] = useState<AttachWorkItemRow[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: 'loading' });
    void (async () => {
      // NOTE: `work_item_document_links` has no `org_id` column (pure junction
      // of work_item_id ↔ document_id; verified against the live schema). Org
      // scoping is transitive — `document_id` is itself org-owned and the
      // client can only have obtained an in-org id, so filtering by it already
      // constrains the read to the active org.
      const { data, error } = await supabase
        .from('work_item_document_links')
        .select(
          'work_item_id, work_items(id, name, type, status, updated_at, org_id)'
        )
        .eq('document_id', documentId);
      if (cancelled) return;
      if (error) {
        setLoad({ kind: 'error', message: error.message });
        return;
      }
      const rows: LinkedWorkItem[] = (data ?? [])
        .map((row) => {
          // PostgREST returns the embedded resource as either an object or
          // an array depending on the inferred cardinality of the FK; the
          // generated types model this loosely, so normalize at the boundary.
          const embedded = (row as unknown as { work_items: WorkItemRow | WorkItemRow[] | null })
            .work_items;
          const wi = Array.isArray(embedded) ? embedded[0] ?? null : embedded;
          if (!wi) return null;
          return {
            id: wi.id,
            name: wi.name,
            type: wi.type as AttachWorkItemType,
            status: wi.status,
            updatedAt: wi.updated_at
          };
        })
        .filter((r): r is LinkedWorkItem => r !== null)
        .sort((a, b) => a.name.localeCompare(b.name));
      setLoad({ kind: 'ready', rows });
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId, refreshTick]);

  const linkedIds = useMemo(() => {
    if (load.kind !== 'ready') return new Set<string>();
    return new Set(load.rows.map((r) => r.id));
  }, [load]);

  const openCover = useCallback(async () => {
    const { data, error } = await supabase
      .from('work_items')
      .select('id, name, type, status, updated_at')
      .eq('org_id', orgId)
      .order('updated_at', { ascending: false });
    if (error) {
      onError(error.message);
      return;
    }
    const rows: AttachWorkItemRow[] = (data ?? [])
      .filter((w) => !linkedIds.has(w.id as string))
      .map((w) => ({
        id: w.id as string,
        title: (w.name as string) ?? '',
        type: w.type as AttachWorkItemType,
        status: (w.status as string) ?? '',
        assigneeName: null,
        updatedAt: (w.updated_at as string) ?? new Date().toISOString()
      }));
    setCandidates(rows);
    setCoverOpen(true);
  }, [orgId, linkedIds, onError]);

  const handleAttach = useCallback(
    async (workItemIds: string[]) => {
      if (workItemIds.length === 0) return;
      // No `org_id` on this junction table — the link rows reference a
      // document_id + work_item_id the client could only have obtained in-org,
      // so the insert is already org-gated without an explicit column.
      const rows = workItemIds.map((wid) => ({
        document_id: documentId,
        work_item_id: wid
      }));
      const { error } = await supabase
        .from('work_item_document_links')
        .insert(rows);
      if (error) {
        onError(error.message);
        return;
      }
      setRefreshTick((t) => t + 1);
    },
    [documentId, onError]
  );

  const attachAction = (
    <button
      type="button"
      className="pn-card-head-action"
      onClick={() => void openCover()}
      data-testid="document-editor-v2-attached-work-items-attach"
    >
      + Attach to Work Item
    </button>
  );

  // The naked variant lifts the "+ Attach" action into a leading inline row
  // (the chrome-less wrapper has no card head to host it). The legacy variant
  // continues to embed it in Card.Head actions slot.
  const nakedAttachRow = (
    <div className="dev2-attached-naked-actions">
      <button
        type="button"
        className="dev2-attached-naked-attach"
        onClick={() => void openCover()}
        data-testid="document-editor-v2-attached-work-items-attach"
      >
        + Attach to Work Item
      </button>
    </div>
  );

  const wrap = (
    state: 'loading' | 'error' | 'ready',
    body: ReactNode,
    rowCount?: number
  ) => {
    if (naked) {
      return (
        <div
          className="dev2-attached-naked"
          data-testid="document-editor-v2-attached-work-items"
          data-state={state}
          data-row-count={rowCount}
          aria-label="Attached work items"
        >
          {nakedAttachRow}
          {body}
        </div>
      );
    }
    return (
      <Card
        data-testid="document-editor-v2-attached-work-items"
        data-state={state}
        data-row-count={rowCount}
        aria-label="Attached work items"
      >
        <Card.Head title="Work items" grow actions={attachAction} />
        <Card.Body>{body}</Card.Body>
      </Card>
    );
  };

  if (load.kind === 'loading') {
    return wrap('loading', <Skeleton width="100%" height={32} />);
  }

  if (load.kind === 'error') {
    return wrap(
      'error',
      <div className="dev2-attached-error">{load.message}</div>
    );
  }

  const body = (
    <>
      {load.rows.length === 0 ? (
          <EmptyState
            data-testid="document-editor-v2-attached-work-items-empty"
            action={
              <Button
                variant="primary"
                size="sm"
                onClick={() => void openCover()}
                data-testid="document-editor-v2-attached-work-items-empty-attach"
              >
                + Attach to Work Item
              </Button>
            }
          >
            No work items attached yet.
          </EmptyState>
        ) : (
          <ul className="dev2-attached-rows">
            {load.rows.map((row) => (
              <li
                key={row.id}
                className="dev2-attached-row"
                data-testid={`document-editor-v2-attached-work-items-row-${row.id}`}
              >
                <button
                  type="button"
                  className="dev2-attached-title"
                  onClick={() => navigate(`/work-item-detail/${row.id}`)}
                  data-testid={`document-editor-v2-attached-work-items-title-${row.id}`}
                >
                  {row.name || 'Untitled'}
                </button>
                <div className="dev2-attached-meta">
                  <Tag
                    className="dev2-attached-type"
                    data-testid={`document-editor-v2-attached-work-items-type-${row.id}`}
                  >
                    {row.type}
                  </Tag>
                  <Tag
                    accent
                    className="dev2-attached-status"
                    data-testid={`document-editor-v2-attached-work-items-status-${row.id}`}
                  >
                    {row.status}
                  </Tag>
                </div>
              </li>
            ))}
          </ul>
        )}

      <AttachWorkItemCover
        open={coverOpen}
        onClose={() => setCoverOpen(false)}
        onAttach={handleAttach}
        documentId={documentId}
        workItems={candidates}
      />
    </>
  );

  return wrap('ready', body, load.rows.length);
}
